import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, FloppyDisk } from "@phosphor-icons/react";
import { api, errMsg, COUNTRIES } from "@/lib/api";
import { AppHead, usePropertyList } from "@/pages/app/Dashboard";
import { useT } from "@/i18n";

const empty = { name: "", city: "", country: "Spain", property_type: "apartment", facts: "" };

export default function Properties() {
  const { t } = useT();
  const [props, load] = usePropertyList();
  const [f, setF] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) await api.put(`/properties/${editId}`, f); else await api.post("/properties", f);
      toast.success(editId ? t("Property updated") : t("Property saved"));
      setF(empty); setEditId(null); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const remove = async (id) => { await api.delete(`/properties/${id}`); toast.success(t("Deleted")); load(); };

  return (
    <div data-testid="properties-page">
      <AppHead title="Saved properties" sub="Save the facts once. Every tool can reuse them." />
      <div className="grid lg:grid-cols-[400px_1fr] gap-6 items-start">
        <form onSubmit={save} className="card p-6 bg-white grid gap-3" data-testid="property-form">
          <div><span className="label">{t("Name")}</span><input required className="field" value={f.name} onChange={set("name")} placeholder="Casa Azul" data-testid="property-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><span className="label">{t("City / area")}</span><input className="field" value={f.city} onChange={set("city")} placeholder="Valencia, Ruzafa" data-testid="property-city-input" /></div>
            <div><span className="label">{t("Country")}</span><select className="field" value={f.country} onChange={set("country")} data-testid="property-country-select">{COUNTRIES.map((o) => <option key={o} value={o}>{t(o)}</option>)}</select></div>
          </div>
          <div><span className="label">{t("Type")}</span><select className="field" value={f.property_type} onChange={set("property_type")} data-testid="property-type-select">{["apartment", "house", "villa", "studio", "room", "cottage"].map((o) => <option key={o} value={o}>{t(o)}</option>)}</select></div>
          <div><span className="label">{t("Property facts")}</span><textarea rows={6} className="field resize-none" value={f.facts} onChange={set("facts")} placeholder={t("2 bedrooms (1 queen, 2 singles), balcony, 5 min to metro, fast Wi-Fi 300 Mbps, AC…")} data-testid="property-facts-input" /></div>
          <div className="flex gap-2">
            <button disabled={busy} className="btn-coral" data-testid="property-save-btn">{editId ? <FloppyDisk size={18} /> : <Plus size={18} />} {editId ? t("Update") : t("Save property")}</button>
            {editId && <button type="button" onClick={() => { setEditId(null); setF(empty); }} className="btn-ghost" data-testid="property-cancel-edit-btn">{t("Cancel")}</button>}
          </div>
        </form>
        <div className="grid gap-4" data-testid="property-list">
          {props.length === 0 && <div className="card p-8 text-gray-500">{t("No properties yet. Add your first one on the left.")}</div>}
          {props.map((p) => (
            <div key={p.property_id} className="card p-6 bg-white" data-testid={`property-item-${p.property_id}`}>
              <div className="flex justify-between gap-4">
                <div><p className="font-display text-xl text-[#0F4C5C]">{p.name}</p><p className="text-sm text-gray-500">{t(p.property_type)} · {p.city} {p.country}</p></div>
                <div className="flex gap-2">
                  <button onClick={() => { setEditId(p.property_id); setF({ name: p.name, city: p.city, country: p.country, property_type: p.property_type, facts: p.facts }); }} className="text-[#0F4C5C] hover:text-[#E36414]" data-testid={`property-edit-${p.property_id}`}><PencilSimple size={18} /></button>
                  <button onClick={() => remove(p.property_id)} className="text-[#0F4C5C] hover:text-red-600" data-testid={`property-delete-${p.property_id}`}><Trash size={18} /></button>
                </div>
              </div>
              {p.facts && <p className="text-sm text-gray-700 mt-3 line-clamp-3">{p.facts}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
